// Pure helpers for the Website Studio preview protocol (WEBSITE_BUILD.md,
// contract 1). No Vue, no DOM, no import.meta: previewBridge.js and
// studioOverlay.js use these in the browser, and tests/studioLogic.test.js
// runs them under plain `node --test`.

import { filmstripItemType, hasImage, hasVideo } from "./sectionMedia.js";

export const STUDIO_MODES = ["setup", "edit", "off"];
export const MAX_SECTIONS = 200;
const MAX_ID = 200;
const MAX_LABEL = 60;

const isObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v) => (typeof v === "string" ? v : "");

// ---------------------------------------------------------------- paths

// '/about/' -> '/about', '' -> '/', drops ?query and #hash.
export function normalisePath(path) {
    if (typeof path !== "string") return null;
    let p = path.split(/[?#]/)[0].trim();
    if (!p.startsWith("/")) p = `/${p}`;
    if (p.length > 1) p = p.replace(/\/+$/, "");
    return p || "/";
}

// ---------------------------------------------------------------- images

// The photo a section slot shows: a Pexels pick uses pexels.src.large;
// otherwise Tokyo's already-signed displayUrl, then url. '' when none.
export function pickImageUrl(image) {
    if (!isObject(image)) return "";
    const large = image.pexels?.src?.large;
    if (typeof large === "string" && large) return large;
    if (typeof image.displayUrl === "string" && image.displayUrl) return image.displayUrl;
    if (typeof image.url === "string") return image.url;
    return "";
}

// Same rule for a video (pexels videos keep their link; the hero picks the
// file from video.pexels itself).
function normaliseVideo(video) {
    if (!isObject(video)) return { link: "" };
    const out = { ...video };
    const shown = str(video.displayUrl) || str(video.link) || str(video.url);
    out.link = shown;
    if ("url" in out && typeof out.url !== "string") delete out.url;
    return out;
}

function normaliseImage(image) {
    if (!isObject(image)) return { url: "" };
    return { ...image, url: pickImageUrl(image) };
}

// A filmstrip renders a list of URL strings (pages.js does the same for the
// saved page); Tokyo sends attachments.
function filmstripUrl(item) {
    if (typeof item === "string") return item;
    if (!isObject(item)) return "";
    const kind = filmstripItemType(item);
    if (kind === "pexels") return typeof item.url?.src?.large === "string" ? item.url.src.large : "";
    // A strip shows pictures only (the live render does the same).
    if (kind === "video" || kind === "file") return "";
    if (isObject(item.image)) return pickImageUrl(item.image);
    if (typeof item.displayUrl === "string" && item.displayUrl) return item.displayUrl;
    if (typeof item.url === "string") return item.url;
    return "";
}

const IMAGE_TYPES = new Set(["hero", "split", "content", "areas"]);

// Turns one section from Tokyo's draft into the shape the renderer expects
// (what pages.js produces for a saved page). Returns null for junk.
export function normaliseSection(section) {
    if (!isObject(section) || typeof section.type !== "string" || !section.type) return null;
    // A structured clone keeps the store free of anything shared with the message.
    let s;
    try {
        s = JSON.parse(JSON.stringify(section));
    } catch {
        return null;
    }
    if (s.id !== undefined && typeof s.id !== "string") s.id = s.id === null ? undefined : String(s.id);
    if (IMAGE_TYPES.has(s.type)) {
        s.image = normaliseImage(s.image);
        if (s.type !== "areas") s.video = normaliseVideo(s.video);
        if (typeof s.title !== "string") s.title = "";
        if (typeof s.content !== "string") s.content = "";
    }
    switch (s.type) {
        case "image-grid":
            s.images = (Array.isArray(s.images) ? s.images : []).filter(isObject).map((item) => ({
                ...item,
                image: normaliseImage(item.image),
                video: normaliseVideo(item.video),
            }));
            break;
        case "filmstrip":
            s.images = (Array.isArray(s.images) ? s.images : []).map(filmstripUrl).filter(Boolean);
            break;
        case "features":
            if (!Array.isArray(s.features)) s.features = [];
            break;
        case "testimonial":
            if (!Array.isArray(s.content)) s.content = [];
            break;
        case "faq":
            s.title = str(s.title);
            s.items = (Array.isArray(s.items) ? s.items : [])
                .filter(isObject)
                .map((i) => ({ question: str(i.question), answer: str(i.answer) }));
            break;
        case "areas":
            s.areas = (Array.isArray(s.areas) ? s.areas : []).filter((a) => typeof a === "string" && a.trim());
            break;
        case "links":
            if (!Array.isArray(s.links)) s.links = [];
            break;
        default:
            break;
    }
    return s;
}

// Pexels photographer credits, as pages.js collects them for the footer.
export function collectPexels(value, out = []) {
    if (Array.isArray(value)) {
        value.forEach((v) => collectPexels(v, out));
    } else if (isObject(value)) {
        if (isObject(value.pexels) && (value.pexels.photographer || value.pexels.user)) out.push(value.pexels);
        Object.values(value).forEach((v) => collectPexels(v, out));
    }
    return out;
}

// ---------------------------------------------------------------- hidden

export const isShown = (section) => isObject(section) && section.hidden !== true;

// What the renderer draws: the visible sections, each with its index in the
// full list (hidden ones included, which is how Tokyo counts) and a stable,
// unique key for v-for (ids repeat on About and Contact: features, features#2).
export function shownSections(sections) {
    if (!Array.isArray(sections)) return [];
    const seen = new Map();
    const out = [];
    sections.forEach((section, index) => {
        if (!isShown(section)) return;
        const base = typeof section.id === "string" && section.id ? section.id : `${section.type || "section"}@${index}`;
        const n = (seen.get(base) || 0) + 1;
        seen.set(base, n);
        out.push({ section, index, key: n === 1 ? base : `${base}#${n}` });
    });
    return out;
}

// A section that draws nothing until it has content (an empty gallery, no
// logos, no questions...) is zero pixels tall, so the studio couldn't outline,
// spotlight or tap it. In studio mode the renderer shows this placeholder in
// its place; null when the section draws something itself.
const hasMedia = (item) => isObject(item) && ((isObject(item.image) && !!item.image.url) || (isObject(item.video) && !!(item.video.link || item.video.url)));
export function emptyPlaceholder(section) {
    if (!isObject(section)) return null;
    switch (section.type) {
        case "image-grid":
            return (Array.isArray(section.images) ? section.images : []).some(hasMedia) ? null : "Your photos go here";
        case "filmstrip":
            return (Array.isArray(section.images) ? section.images : []).some((i) => typeof i === "string" && i) ? null : "Your logos and certificates go here";
        case "features":
            return Array.isArray(section.features) && section.features.length > 0 ? null : "What you offer goes here";
        case "testimonial":
            return Array.isArray(section.content) && section.content.length > 0 ? null : "What customers say goes here";
        case "links":
            return Array.isArray(section.links) && section.links.length > 0 ? null : "Your links go here";
        case "faq": {
            const any = (Array.isArray(section.items) ? section.items : []).some((i) => isObject(i) && str(i.question).trim());
            return any || str(section.title).trim() ? null : "Your questions and answers go here";
        }
        case "areas": {
            const any = (Array.isArray(section.areas) ? section.areas : []).some((a) => typeof a === "string" && a.trim());
            return any || str(section.title).trim() || str(section.content).trim() ? null : "The areas you cover go here";
        }
        default:
            return null;
    }
}

// An empty photo slot in a hero or split section: in studio mode the
// renderer marks it "Your photo goes here" so the setup walkthrough can
// point at it. A video fills a hero's slot too. Only the slots the studio
// asks for (bristol domain/websiteSections.js SLOTS): Words has no photo
// there, so marking one would point at something nobody can fill.
const PHOTO_SLOT_TYPES = new Set(["hero", "split"]);
export function photoSlotEmpty(section) {
    if (!isObject(section) || !PHOTO_SLOT_TYPES.has(section.type)) return false;
    // The Our work page's header is words only (no photo to fill).
    if (section.id === "portfolio-header") return false;
    if (hasImage(section.image)) return false;
    if (section.type !== "split" && hasVideo(section.video)) return false;
    return true;
}

// ---------------------------------------------------------------- messages

const cleanId = (v) => (typeof v === "string" && v.length > 0 && v.length <= MAX_ID ? v : null);

export function cleanLabel(v) {
    if (typeof v !== "string") return "";
    const t = v.replace(/\s+/g, " ").trim();
    return t.length > MAX_LABEL ? `${t.slice(0, MAX_LABEL - 1)}…` : t;
}

export function normaliseDraftPage(page) {
    if (!isObject(page) || !Array.isArray(page.sections)) return null;
    const sections = page.sections.slice(0, MAX_SECTIONS).map(normaliseSection).filter(Boolean);
    return {
        title: typeof page.title === "string" ? page.title : null,
        description: typeof page.description === "string" ? page.description : null,
        sections,
    };
}

// Validates a Tokyo -> naples studio message. Returns a clean copy, or null
// for anything that isn't one (or is malformed).
export function parseStudioMessage(data) {
    if (!isObject(data) || typeof data.type !== "string") return null;
    switch (data.type) {
        case "draksy-studio-mode":
            return STUDIO_MODES.includes(data.mode) ? { type: data.type, mode: data.mode } : null;
        case "draksy-studio-select":
            if (data.id !== null && !cleanId(data.id)) return null;
            return { type: data.type, id: data.id === null ? null : data.id, scroll: data.scroll === true, label: cleanLabel(data.label) };
        case "draksy-studio-spotlight":
            if (data.id !== null && !cleanId(data.id)) return null;
            return { type: data.type, id: data.id === null ? null : data.id };
        case "draksy-studio-insert-points":
            return typeof data.show === "boolean" ? { type: data.type, show: data.show } : null;
        case "draksy-studio-page": {
            const path = normalisePath(data.path);
            if (!path) return null;
            // page: null drops a draft held for that path (an extension).
            if (data.page === null) return { type: data.type, path, page: null };
            const page = normaliseDraftPage(data.page);
            return page ? { type: data.type, path, page } : null;
        }
        default:
            return null;
    }
}

// ---------------------------------------------------------------- geometry

// A client rect -> document coordinates (whole pixels).
export function toDocumentRect(rect, scrollX = 0, scrollY = 0) {
    return {
        top: Math.round(rect.top + scrollY),
        left: Math.round(rect.left + scrollX),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
    };
}

// Where the "+ Add a section here" lines go. `placed` is the visible
// sections in page order: [{index (in the full list), top, height}] in
// document coordinates. A line after a section inserts right after it
// (index + 1); the last line inserts at the end of the full list. With no
// visible sections, emptyY (where the empty page's note sits) gets one line.
export function insertPoints(placed, totalLength, emptyY) {
    const rows = (placed || []).filter((p) => p && Number.isFinite(p.top) && Number.isFinite(p.height));
    if (!rows.length) return Number.isFinite(emptyY) ? [{ index: totalLength, y: Math.round(emptyY) }] : [];
    return rows.map((p, i) => {
        const bottom = p.top + p.height;
        const next = rows[i + 1];
        if (!next) return { index: totalLength, y: bottom };
        return { index: p.index + 1, y: Math.round((bottom + next.top) / 2) };
    });
}

// Scroll position that shows a section: centred when it fits below the top
// inset (the site's fixed bar), else its top just under the inset.
export function scrollTopFor(top, height, viewportHeight, topInset = 0) {
    const room = viewportHeight - topInset;
    const target = height <= room ? top - topInset - (room - height) / 2 : top - topInset;
    return Math.max(0, Math.round(target));
}

// ---------------------------------------------------------------- timing

export function debounce(fn, wait) {
    let timer = null;
    const run = (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            fn(...args);
        }, wait);
    };
    run.cancel = () => clearTimeout(timer);
    return run;
}

// Leading and trailing: the first call runs now, the last one in a burst
// still runs once the wait is up.
export function throttle(fn, wait, now = () => Date.now()) {
    let last = -Infinity;
    let timer = null;
    let latest = [];
    const run = (...args) => {
        latest = args;
        const remaining = wait - (now() - last);
        if (remaining <= 0) {
            clearTimeout(timer);
            timer = null;
            last = now();
            fn(...args);
        } else if (!timer) {
            timer = setTimeout(() => {
                timer = null;
                last = now();
                fn(...latest);
            }, remaining);
        }
    };
    run.cancel = () => clearTimeout(timer);
    return run;
}
